import { useEffect, useMemo, useState } from "react";

// Consolidates the pagination state pattern used across Summary, Leaderboard,
// PnlCalendar, etc.
//
// Inputs:
//   data — the latest paginated server response. Expected shape:
//     { page, count, numrows, ... }   (camelCase or snake_case both ok)
//   defaultPageSize — initial value of the page-size selector (numResults)
//
// Returns:
//   page, totalCount, numRows           — synced from data
//   numResults, setNumResults           — page-size selector state
//   startOffset, endOffset              — display range (1-based start)
//   canGoNext, canGoPrev                — derived enable flags
//
// Usage:
//   const pg = usePagination(trades, { defaultPageSize: 100 });
//   <Button isDisabled={!pg.canGoNext} onClick={...}>Next</Button>
//   <Text>Showing {pg.startOffset}-{pg.endOffset} of {pg.totalCount}</Text>
export default function usePagination(data, { defaultPageSize = 100 } = {}) {
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [numRows, setNumRows] = useState(0);
  const [numResults, setNumResults] = useState(defaultPageSize);

  useEffect(() => {
    if (!data) return;
    if (
      data.page !== undefined &&
      data.count !== undefined &&
      data.numrows !== undefined
    ) {
      setPage(parseInt(data.page, 10));
      setTotalCount(parseInt(data.count, 10));
      setNumRows(parseInt(data.numrows, 10));
    }
  }, [data]);

  const startOffset = useMemo(
    () => (page !== 0 ? page * numRows - (numRows - 1) : 0),
    [page, numRows]
  );
  const endOffset = useMemo(
    () => (totalCount < page * numRows ? totalCount : page * numRows),
    [page, numRows, totalCount]
  );

  const canGoPrev = page > 1;
  const canGoNext = useMemo(() => {
    if (totalCount <= 0 || numRows <= 0) return false;
    const pageCount = Math.ceil(totalCount / numRows);
    return page < pageCount;
  }, [page, numRows, totalCount]);

  return {
    page,
    totalCount,
    numRows,
    numResults,
    setNumResults,
    startOffset,
    endOffset,
    canGoNext,
    canGoPrev,
  };
}
